import classNames from 'classnames';
import type { MemoryDndItem } from 'constants/memory-dnd';
import MemoryDnD from 'constants/memory-dnd';
import React from 'react';
import { useDrop } from 'react-dnd';
import { connect } from 'react-redux';
import { toggleHourTimestampFromMemory } from 'redux/modules/hours.actions';
import {
  deleteEntryIds,
  markEntryIdsAsNotSpam,
  markEntryIdsAsSpam,
  toggleExpandEntryId,
  toggleExpandGroupRef,
} from 'redux/modules/timeline.actions';
import {
  disassociateMemories,
  handleDroppedMemory,
  removeEntryIdsOnHourDraft,
  toggleEntryIdsOnHourDraft,
} from 'redux/modules/timeline.memory.actions';
import type { HourEditorProps } from 'redux/selectors/hours.draft';
import { getDraftMemorySectionProps } from 'redux/selectors/timeline.memories';
import type { IState } from 'redux/state';
import { MemoryGroup } from 'routes/supercalendar/components/calendar/memory/MemoryGroup';
import styles from 'routes/supercalendar/components/calendar/PendingDraft.module.scss';
import type { MappedActions } from 'types/redux';
import { OperatorMode } from 'types/timeline';
import DropTargetPlaceholder from '../DropTargetPlaceholder';
import s from './MemorySection.module.scss';

interface ExternalProps {
  date: string;
  showDebugInfo: boolean;
  standalone?: boolean;
  className?: string;
  linkedMemoriesCount: number;
  ui: HourEditorProps['ui'];
  scrollTop?: number;
  setIsDragging?: (isDragging: boolean) => void;
}

const mapStateToProps = (state: IState) => getDraftMemorySectionProps(state);

type StateProps = ReturnType<typeof mapStateToProps>;
type ActionProps = MappedActions<typeof mapActionsToProps>;

const mapActionsToProps = {
  toggleExpandGroupRef,
  toggleExpandEntryId,
  markEntryIdsAsSpam,
  markEntryIdsAsNotSpam,
  deleteEntryIds,
  toggleEntryIdsOnHourDraft,
  removeEntryIdsOnHourDraft,
  toggleHourTimestampFromMemory,
  onDropMemory: handleDroppedMemory,
  onDisassociateMemoryIds: disassociateMemories,
};

type Props = ExternalProps & StateProps & ActionProps;

const MemorySection = (props: Props) => {
  const { onDropMemory, setIsDragging } = props;

  const [dropProps, dropRef] = useDrop(
    () => ({
      accept: MemoryDnD.TYPE,
      drop: (item: MemoryDndItem) => {
        onDropMemory({
          memoryIds: item.memoryIds,
          source: item.source,
          target: {
            area: MemoryDnD.AREA.HourDraft,
          },
        });
      },
      canDrop: (item: MemoryDndItem) => {
        return item.source.area !== MemoryDnD.AREA.HourDraft;
      },
      collect: monitor => {
        const canDrop = monitor.canDrop();
        if (setIsDragging) {
          setIsDragging(canDrop);
        }
        return {
          canDrop,
          highlightDrop: monitor.canDrop() && monitor.isOver(),
        };
      },
    }),
    [onDropMemory, setIsDragging]
  );

  const noLinkedMemories = props.linkedMemoriesCount === 0;
  const singleMemoryHeight = 52;
  const groupCount = props.memoryGroups.length;
  const inlineGroupHeight = groupCount >= 5 ? 330 : groupCount * singleMemoryHeight + 50;

  function renderPlaceholder() {
    return (
      <DropTargetPlaceholder
        className={classNames(s.dropContainer, {
          [s.canDrop]: dropProps.canDrop,
          [s.standaloneDrop]: props.standalone,
          [s.noSelectedProject]: !props.ui.hasSelectedProject,
        })}
        style={{
          transform: `translateY(${props.scrollTop || 0}px)`,
          top: 0,
          maxHeight: props.standalone ? inlineGroupHeight : 'auto',
        }}
        canDrop={dropProps.canDrop}
        highlightDrop={dropProps.highlightDrop}
        ref={dropRef}
      >
        Drag memories here to log them
      </DropTargetPlaceholder>
    );
  }

  if (noLinkedMemories && !props.ui.showMemories && !props.standalone) {
    return null;
  }

  if (noLinkedMemories && (props.ui.showMemories || props.standalone) && !props.ui.isBilled) {
    return (
      <>
        <div className={styles.noMemoriesText}>
          <p>Click or drag memories to add them to this entry.</p>
        </div>
        {renderPlaceholder()}
      </>
    );
  }

  return (
    <div
      className={classNames(s.scrollbars, {
        [s.standalone]: props.standalone,
      })}
    >
      <div
        className={classNames(styles.container, s.container, props.className, {
          [s.standaloneContainer]: props.standalone,
        })}
      >
        {props.memoryGroups.map(group => (
          <MemoryGroup
            key={group.ref}
            dragSource={{ area: MemoryDnD.AREA.HourDraft }}
            group={group}
            hasEnabledTic={props.ui.hasEnabledTic}
            toggleHourTimestampFromMemory={props.toggleHourTimestampFromMemory}
            toggleExpandGroupRef={props.toggleExpandGroupRef}
            toggleExpandEntryId={props.toggleExpandEntryId}
            markEntryIdsAsSpam={props.markEntryIdsAsSpam}
            markEntryIdsAsNotSpam={props.markEntryIdsAsNotSpam}
            toggleSelectEntryIds={props.toggleEntryIdsOnHourDraft}
            deleteEntryIds={props.deleteEntryIds}
            onDisassociateMemoryIds={props.onDisassociateMemoryIds}
            removeHourDraftEntryIds={props.removeEntryIdsOnHourDraft}
            isLocked={props.ui.isLocked}
            groupSection="selected"
            showDebugInfo={props.showDebugInfo}
            operatorMode={OperatorMode.withHourDraft}
          />
        ))}
      </div>
      {renderPlaceholder()}
    </div>
  );
};

const ConnectedComponent: React.ComponentType<ExternalProps> = connect(
  mapStateToProps,
  mapActionsToProps
)(MemorySection);
export default ConnectedComponent;
